import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { AuthLayout } from './AuthLayout';
import { signup } from '@/api/authApi';
import { setAuthToken, setAuthUser } from '@/lib/auth';

export const SignupView: React.FC = () => {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const fromState =
    (location.state as { from?: Location } | undefined)?.from?.pathname || '';
  const fromQuery = new URLSearchParams(location.search).get('from') || '';
  const from = fromState || fromQuery || '/';
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const validateSignup = () => {
    const name = fullName.trim();
    const cleanedEmail = email.trim().toLowerCase();
    const trimmedPassword = password;

    if (name.length < 2 || name.length > 60) {
      return 'Full name must be 2-60 characters.';
    }
    if (!emailRegex.test(cleanedEmail)) {
      return 'Please enter a valid email address.';
    }
    if (trimmedPassword.length < 8 || trimmedPassword.length > 72) {
      return 'Password must be 8-72 characters.';
    }
    if (!/[A-Z]/.test(trimmedPassword)) {
      return 'Password must include at least one uppercase letter.';
    }
    if (!/[a-z]/.test(trimmedPassword)) {
      return 'Password must include at least one lowercase letter.';
    }
    if (!/[0-9]/.test(trimmedPassword)) {
      return 'Password must include at least one number.';
    }
    if (!/[^A-Za-z0-9]/.test(trimmedPassword)) {
      return 'Password must include at least one symbol.';
    }
    return null;
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreed || loading) return;
    setError(null);

    const validationError = validateSignup();
    if (validationError) {
      setError(validationError);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    try {
      setLoading(true);
      const payload = await signup({
        email: email.trim().toLowerCase(),
        password,
        full_name: fullName.trim(),
      });
      const user = payload.user;
      if (user?.status === 'approved') {
        setAuthToken(payload.access_token);
        setAuthUser(user);
        navigate(from, { replace: true });
      } else {
        setAuthToken(null);
        setAuthUser(null);
        setError('Account created. Please wait for admin approval, then log in.');
      }
    } catch (err: any) {
      setError(err?.message || 'Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <div className="text-center">
        <h2 className="text-3xl font-bold tracking-tight text-gray-900">Create Account</h2>
        <p className="mt-2 text-sm text-gray-600">Start using AI-powered health insights</p>
      </div>

      <div className="mt-8 space-y-6 w-full">
        <form onSubmit={handleSignup} className="space-y-4">
          <div>
            <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
            <input
              id="fullName"
              type="text"
              required
              placeholder="Your full name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="block w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500 transition-all"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              id="email"
              type="email"
              required
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="block w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500 transition-all"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input
              id="password"
              type="password"
              required
              placeholder="Create a password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="block w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500 transition-all"
            />
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
            <input
              id="confirmPassword"
              type="password"
              required
              placeholder="Confirm your password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="block w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500 transition-all"
            />
          </div>

          <div className="flex items-start gap-3">
            <input
              id="terms"
              type="checkbox"
              required
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="terms" className="text-sm text-gray-600">
              I agree to the <Link to="/terms" className="text-blue-600 font-medium">Terms</Link> and <Link to="/privacy" className="text-blue-600 font-medium">Privacy</Link>
            </label>
          </div>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={!agreed || loading}
            className="flex w-full justify-center rounded-xl bg-blue-500 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-blue-600 transition-all disabled:opacity-50"
          >
            {loading ? "Creating account..." : "Create account"}
          </button>
        </form>

        <div className="text-center text-sm">
          <span className="text-gray-600">Already have an account? </span>
          <Link to="/login" className="font-semibold text-blue-600 hover:text-blue-500">Login</Link>
        </div>
      </div>

      {/* Firebase OTP removed in favor of email/password auth */}
    </AuthLayout>
  );
};
